import { useState, useEffect, useRef } from "react";
import {
  Plus,
  Search,
  Phone,
  Mail,
  Calendar,
  Users,
  Edit2,
  Trash2,
  Eye,
  Download,
  Upload,
  FileText,
  CheckCircle,
  AlertCircle,
  X,
  GitMerge,
  AlertTriangle,
  Ban,
  SlidersHorizontal,
  XCircle,
} from "lucide-react";
import { format } from "date-fns";
import api from "../lib/api-client";
import GoogleContactsSection from "../components/GoogleContactsSection";
import CustomerDetailModal from "../components/CustomerDetailModal";
import CustomerEditModal, {
  EmailStatusBadge,
  type Customer,
} from "../components/CustomerEditModal";
import { getSourceConfig } from "../lib/reservationSources";
import { formatCents } from "../lib/money";
import { safeFormatDate } from "../lib/dateFormat";
import { useGlobalToast } from "../contexts/ToastContext";
import { useBookingMode } from "../contexts/LocationContext";
import { usePermissions } from "../hooks/usePermissions";
import {
  EMAIL_STATUS_FILTER_OPTIONS,
  type CustomerEmailStatus,
  type EmailStatusCounts,
} from "../lib/customerEmailStatus";

/**
 * Extrae el mensaje de error de una respuesta axios cuando la petición se
 * hizo con `responseType: "blob"` (descarga/exportación CSV): el cuerpo de
 * error (incluido el JSON `{ message }` del backend en un 403/500) llega
 * como `Blob`, no como objeto — hay que leerlo como texto y parsear antes de
 * poder mostrarlo. Si no se puede parsear, cae a un mensaje genérico (con
 * caso especial para 403, ya que el backend restringe export a owner/admin).
 */
async function extractBlobErrorMessage(
  error: unknown,
  fallback: string,
): Promise<string> {
  if (error && typeof error === "object" && "response" in error) {
    const response = (
      error as { response?: { data?: unknown; status?: number } }
    ).response;
    const data = response?.data;
    if (data instanceof Blob) {
      try {
        const text = await data.text();
        const parsed = JSON.parse(text) as { message?: string };
        if (parsed?.message) return parsed.message;
      } catch {
        // Cuerpo no parseable como JSON: se usa el fallback de abajo.
      }
    }
    if (response?.status === 403) {
      return "No tienes permiso para realizar esta acción (requiere rol de administrador).";
    }
  }
  return fallback;
}

/**
 * Chip "reservó como «X»": el cliente ha reservado (widget/app) con un nombre
 * que no cuadra con su ficha — una comida de empresa, un grupo, o una ficha
 * mal emparejada. El staff decide si corregir la ficha.
 */
function BookingNameMismatchBadge({ names }: { names?: string[] }) {
  if (!names || names.length === 0) return null;
  const [first, ...rest] = names;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 max-w-[14rem] truncate"
      title={`Ha reservado como: ${names.map((n) => `«${n}»`).join(", ")}. Si la ficha está mal, corrígela.`}
    >
      <AlertTriangle className="w-3 h-3 flex-shrink-0" />
      reservó como «{first}»{rest.length > 0 ? ` +${rest.length}` : ""}
    </span>
  );
}

interface DuplicateGroup {
  key: string;
  reason: string;
  customers: Customer[];
}

export default function Customers() {
  const { showToast } = useGlobalToast();
  const { can } = usePermissions();
  // "clientes:exportar" cubre exportar/importar/fusionar (mismo permiso en el
  // catálogo, ver `lib/permissions.ts`). "clientes:borrar" solo el borrado.
  const canExportCustomers = can("clientes:exportar");
  const canDeleteCustomers = can("clientes:borrar");
  const isAppointmentsMode = useBookingMode() === "appointments";
  const reservationsNoun = isAppointmentsMode ? "citas" : "reservas";
  const reservationsNounCap = isAppointmentsMode ? "Citas" : "Reservas";
  const reservationNounSingular = isAppointmentsMode ? "cita" : "reserva";
  const [searchTerm, setSearchTerm] = useState("");
  // Debounce real: fetchCustomers solo se dispara con este valor, que se
  // actualiza ~300ms después de la última pulsación (ver efecto de abajo).
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  // Error de red/API al cargar el listado (distinto de "no tienes clientes
  // todavía", que es un estado feliz).
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    null,
  );
  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(
    null,
  );
  const [deleteWithReservations, setDeleteWithReservations] = useState<{
    customerId: string;
    reservationsCount: number;
  } | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showDuplicatesModal, setShowDuplicatesModal] = useState(false);
  const [duplicatesCount, setDuplicatesCount] = useState(0);
  // Clientes con reservas hechas "a otro nombre" (contador org-wide).
  const [nameMismatchCount, setNameMismatchCount] = useState(0);
  // Estado de comunicaciones por email: filtro rápido + contadores del encabezado
  const [emailStatusFilter, setEmailStatusFilter] = useState<
    CustomerEmailStatus | ""
  >("");
  const [emailStatusCounts, setEmailStatusCounts] =
    useState<EmailStatusCounts | null>(null);
  // Ficha 360 (al pinchar un cliente)
  const [detailCustomerId, setDetailCustomerId] = useState<string | null>(null);
  // Filtros y ordenación
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<{
    hasReservations: string; // "", "true", "false"
    noShowMin: string; // "", "1", "3"
    blocked: boolean;
    vip: boolean;
    inactive: boolean; // sin visitas en 6 meses
    source: string;
    nameMismatch: boolean; // reservó con otro nombre (bookingNameMismatches)
    spendMin: string; // gasto total mínimo: "", "100", "250"…
    sortBy: string; // "recent" | "reservations" | "noShows" | "lastVisit" | "spend"
  }>({
    hasReservations: "",
    noShowMin: "",
    blocked: false,
    vip: false,
    inactive: false,
    source: "",
    nameMismatch: false,
    spendMin: "",
    sortBy: "recent",
  });
  // Solo con alguna cuenta del TPV guardada: el resto no ve nada de gasto.
  const [spendEnabled, setSpendEnabled] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  const activeFilterCount =
    (filters.hasReservations ? 1 : 0) +
    (filters.noShowMin ? 1 : 0) +
    (filters.blocked ? 1 : 0) +
    (filters.vip ? 1 : 0) +
    (filters.inactive ? 1 : 0) +
    (filters.source ? 1 : 0) +
    (filters.nameMismatch ? 1 : 0) +
    (filters.spendMin ? 1 : 0);

  const hasActiveSearchOrFilters =
    Boolean(searchTerm) || activeFilterCount > 0 || Boolean(emailStatusFilter);

  // Fetch duplicates count (contador global de la organización, ignora
  // búsqueda/filtros). Solo se llama al montar y tras mutaciones reales
  // (crear/editar/eliminar/importar/fusionar) — ver `refreshAfterMutation`.
  const fetchDuplicatesCount = async () => {
    try {
      const response = await api.get("/customers/duplicates");
      setDuplicatesCount(response.data.data?.totalGroups || 0);
    } catch (error) {
      console.error("Error fetching duplicates:", error);
    }
  };

  // Clientes que han reservado con un nombre distinto al de su ficha (org-wide,
  // como el contador de duplicados). Alimenta el botón del encabezado.
  const fetchNameMismatchCount = async () => {
    try {
      const response = await api.get("/customers/name-mismatch-count");
      setNameMismatchCount(response.data.data?.total || 0);
    } catch (error) {
      console.error("Error fetching name mismatch count:", error);
    }
  };

  // Contadores del encabezado por estado de comunicaciones (org-wide, ignora
  // búsqueda/filtros — igual que `fetchDuplicatesCount`).
  const fetchEmailStatusCounts = async () => {
    try {
      const response = await api.get("/customers/email-status-counts");
      setEmailStatusCounts(response.data.data ?? null);
    } catch (error) {
      console.error("Error fetching email status counts:", error);
    }
  };

  // Fetch customers
  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = {
        page: pagination.page,
        limit: pagination.limit,
      };
      if (debouncedSearchTerm) params.search = debouncedSearchTerm;
      if (filters.hasReservations)
        params.hasReservations = filters.hasReservations;
      if (filters.noShowMin) params.noShowMin = filters.noShowMin;
      if (filters.blocked) params.blocked = "true";
      if (filters.vip) params.vip = "true";
      if (filters.inactive) {
        // Inactivo = sin reservas en los últimos 6 meses.
        const d = new Date();
        d.setMonth(d.getMonth() - 6);
        params.inactiveSince = d.toISOString().slice(0, 10);
      }
      if (filters.source) params.source = filters.source;
      if (filters.nameMismatch) params.nameMismatch = "true";
      if (filters.spendMin) params.spendMin = filters.spendMin;
      if (filters.sortBy && filters.sortBy !== "recent")
        params.sortBy = filters.sortBy;
      if (emailStatusFilter) params.emailStatus = emailStatusFilter;

      const response = await api.get("/customers", { params });
      setCustomers(response.data.data || []);
      setSpendEnabled(Boolean(response.data.spendEnabled));
      setLoadError(null);
      if (response.data.pagination) {
        setPagination((prev) => ({
          ...prev,
          total: response.data.pagination.total,
          totalPages: response.data.pagination.totalPages,
        }));
      }
    } catch (error) {
      console.error("Error fetching customers:", error);
      const axiosError = error as { response?: { data?: { message?: string } } };
      const message =
        axiosError.response?.data?.message ||
        "No se pudieron cargar los clientes. Comprueba tu conexión e inténtalo de nuevo.";
      setLoadError(message);
      setCustomers([]);
      showToast(message, "error");
    } finally {
      setLoading(false);
    }
  };

  // Refresca lista + contadores globales tras una mutación real (crear,
  // editar, eliminar, importar, fusionar). Los contadores NUNCA se
  // recalculan en cada tecleo/filtro (ver efectos de abajo).
  const refreshAfterMutation = () => {
    fetchCustomers();
    fetchDuplicatesCount();
    fetchEmailStatusCounts();
    fetchNameMismatchCount();
  };

  // Debounce real de la búsqueda: `debouncedSearchTerm` (y por tanto el
  // fetch del listado) solo se actualiza ~300ms después de la última
  // pulsación, no en cada tecla.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPagination((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch del listado: depende del término YA debounced, la página y los
  // filtros — nunca de `searchTerm` directamente.
  useEffect(() => {
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, debouncedSearchTerm, filters, emailStatusFilter]);

  // Contadores globales (duplicados, estado de email): solo al montar. NO
  // dependen de la búsqueda ni de los filtros — son contadores de toda la
  // organización, no de la vista filtrada actual.
  useEffect(() => {
    fetchDuplicatesCount();
    fetchEmailStatusCounts();
    fetchNameMismatchCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Al cambiar filtros, volver a la página 1.
  useEffect(() => {
    setPagination((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }));
  }, [filters, emailStatusFilter]);

  const handleClearSearchAndFilters = () => {
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setEmailStatusFilter("");
    setFilters({
      hasReservations: "",
      noShowMin: "",
      blocked: false,
      vip: false,
      inactive: false,
      source: "",
      nameMismatch: false,
      spendMin: "",
      sortBy: filters.sortBy,
    });
  };

  // Delete customer
  const handleDelete = async (id: string, force: boolean = false) => {
    try {
      const url = force ? `/customers/${id}?force=true` : `/customers/${id}`;
      await api.delete(url);
      setShowDeleteConfirm(null);
      setDeleteWithReservations(null);
      refreshAfterMutation();
    } catch (error) {
      console.error("Error deleting customer:", error);
      const axiosError = error as {
        response?: {
          data?: {
            message?: string;
            hasReservations?: boolean;
            reservationsCount?: number;
          };
        };
      };

      // Check if the error is because customer has reservations
      if (axiosError.response?.data?.hasReservations) {
        // Show second confirmation modal
        setShowDeleteConfirm(null);
        setDeleteWithReservations({
          customerId: id,
          reservationsCount: axiosError.response.data.reservationsCount || 0,
        });
      } else {
        const errorMessage =
          axiosError.response?.data?.message || "Error al eliminar cliente";
        showToast(errorMessage, "error");
        setShowDeleteConfirm(null);
      }
    }
  };

  // Download CSV template
  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get("/customers/template", {
        responseType: "blob",
      });
      const blob = new Blob([response.data], {
        type: "text/csv;charset=utf-8;",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "plantilla_clientes.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading template:", error);
      showToast(
        await extractBlobErrorMessage(
          error,
          "No se pudo descargar la plantilla",
        ),
        "error",
      );
    }
  };

  // Export customers to CSV
  const handleExportCustomers = async () => {
    try {
      const response = await api.get("/customers/export", {
        responseType: "blob",
      });
      const blob = new Blob([response.data], {
        type: "text/csv;charset=utf-8;",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `clientes_${format(new Date(), "yyyy-MM-dd")}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting customers:", error);
      showToast(
        await extractBlobErrorMessage(
          error,
          "No se pudo exportar el listado de clientes",
        ),
        "error",
      );
    }
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 md:gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">
            Clientes
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            {pagination.total} clientes en tu base de datos
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Duplicates Button (fusionar) - shown solo si hay duplicados Y
              el usuario puede exportar/importar/fusionar clientes */}
          {duplicatesCount > 0 && canExportCustomers && (
            <button
              onClick={() => setShowDuplicatesModal(true)}
              className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-amber-50 border border-amber-300 text-amber-700 font-medium rounded-lg hover:bg-amber-100 transition-colors text-sm"
              title="Ver posibles duplicados"
            >
              <AlertTriangle className="w-4 h-4" />
              <span className="hidden sm:inline">
                {duplicatesCount} duplicados
              </span>
              <span className="sm:hidden">{duplicatesCount}</span>
            </button>
          )}
          {/* Reservas "a otro nombre": abre el listado filtrado para revisar fichas */}
          {nameMismatchCount > 0 && !isAppointmentsMode && (
            <button
              onClick={() => {
                setShowFilters(true);
                setFilters((f) => ({ ...f, nameMismatch: true }));
              }}
              className={`inline-flex items-center justify-center gap-2 px-3 py-2 border font-medium rounded-lg transition-colors text-sm ${
                filters.nameMismatch
                  ? "bg-amber-100 border-amber-400 text-amber-800"
                  : "bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100"
              }`}
              title="Clientes que han reservado con un nombre distinto al de su ficha"
            >
              <AlertTriangle className="w-4 h-4" />
              <span className="hidden sm:inline">
                {nameMismatchCount} con otro nombre
              </span>
              <span className="sm:hidden">{nameMismatchCount}</span>
            </button>
          )}
          {/* Export Button - requiere clientes:exportar */}
          {canExportCustomers && (
            <button
              onClick={handleExportCustomers}
              className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors text-sm"
              title="Exportar clientes a CSV"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Exportar</span>
            </button>
          )}
          {/* Import Button - requiere clientes:exportar */}
          {canExportCustomers && (
            <button
              onClick={() => setShowImportModal(true)}
              className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors text-sm"
              title="Importar clientes desde CSV"
            >
              <Upload className="w-4 h-4" />
              <span className="hidden sm:inline">Importar</span>
            </button>
          )}
          {/* Add Customer Button */}
          <button
            onClick={() => {
              setSelectedCustomer(null);
              setShowModal(true);
            }}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors text-sm md:text-base"
          >
            <Plus className="w-4 md:w-5 h-4 md:h-5" />
            <span>Añadir</span>
          </button>
        </div>
      </div>

      {/* Google Contacts Sync Section */}
      <GoogleContactsSection onImportComplete={refreshAfterMutation} />

      {/* Search + filtros */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-3 md:p-4 space-y-3">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por nombre, email o teléfono..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm md:text-base"
              />
            </div>
            <button
              onClick={() => setShowFilters((v) => !v)}
              className={`relative inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                showFilters || activeFilterCount > 0
                  ? "border-primary-500 bg-primary-50 text-primary-700"
                  : "border-gray-300 text-gray-600 hover:bg-gray-50"
              }`}
            >
              <SlidersHorizontal className="w-4 h-4" />
              <span className="hidden sm:inline">Filtros</span>
              {activeFilterCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-primary-600 text-white text-xs flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>
          {/* El backend ignora búsquedas de 1 solo carácter (no filtra) para
              evitar consultas demasiado amplias: avisamos con un hint sutil
              en vez de dejar que parezca que la búsqueda no funciona. */}
          {searchTerm.length === 1 && (
            <p className="text-xs text-gray-400 -mt-2">
              Escribe al menos 2 caracteres para buscar.
            </p>
          )}

          {/* Estado de comunicaciones por email */}
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs font-medium text-gray-400 uppercase tracking-wide mr-1">
              Comunicaciones
            </span>
            {EMAIL_STATUS_FILTER_OPTIONS.map((option) => {
              const count =
                option.value === ""
                  ? emailStatusCounts?.total
                  : emailStatusCounts?.[option.value];
              const active = emailStatusFilter === option.value;
              return (
                <button
                  key={option.value || "todos"}
                  onClick={() => setEmailStatusFilter(option.value)}
                  className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${
                    active
                      ? "border-primary-500 bg-primary-50 text-primary-700"
                      : "border-gray-200 text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  {option.label}
                  {typeof count === "number" && <span>({count})</span>}
                </button>
              );
            })}
          </div>

          {showFilters && (
            <div className="border-t border-gray-100 pt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {/* Reservas/Citas */}
              <label className="text-sm">
                <span className="block text-gray-500 mb-1">
                  {reservationsNounCap}
                </span>
                <select
                  value={filters.hasReservations}
                  onChange={(e) =>
                    setFilters((f) => ({
                      ...f,
                      hasReservations: e.target.value,
                    }))
                  }
                  className="w-full border border-gray-300 rounded-lg px-2 py-2 text-sm"
                >
                  <option value="">Todos</option>
                  <option value="true">Con {reservationsNoun}</option>
                  <option value="false">Sin {reservationsNoun}</option>
                </select>
              </label>

              {/* No-shows */}
              <label className="text-sm">
                <span className="block text-gray-500 mb-1">No-shows</span>
                <select
                  value={filters.noShowMin}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, noShowMin: e.target.value }))
                  }
                  className="w-full border border-gray-300 rounded-lg px-2 py-2 text-sm"
                >
                  <option value="">Cualquiera</option>
                  <option value="1">Con 1 o más</option>
                  <option value="3">Con 3 o más</option>
                </select>
              </label>

              {/* Gasto total */}
              {spendEnabled && (
                <label className="text-sm">
                  <span className="block text-gray-500 mb-1">Gasto (24 meses)</span>
                  <select
                    value={filters.spendMin}
                    onChange={(e) =>
                      setFilters((f) => ({ ...f, spendMin: e.target.value }))
                    }
                    className="w-full border border-gray-300 rounded-lg px-2 py-2 text-sm"
                  >
                    <option value="">Cualquiera</option>
                    <option value="100">100 o más</option>
                    <option value="250">250 o más</option>
                    <option value="500">500 o más</option>
                    <option value="1000">1.000 o más</option>
                  </select>
                </label>
              )}

              {/* Canal */}
              <label className="text-sm">
                <span className="block text-gray-500 mb-1">Canal de origen</span>
                <select
                  value={filters.source}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, source: e.target.value }))
                  }
                  className="w-full border border-gray-300 rounded-lg px-2 py-2 text-sm"
                >
                  <option value="">Todos</option>
                  <option value="widget">Web (widget)</option>
                  <option value="web">Web</option>
                  <option value="phone-ai">IA telefónica (Carmen)</option>
                  <option value="mobile_app">App móvil</option>
                  <option value="dashboard">Panel</option>
                  <option value="phone">Teléfono</option>
                  <option value="walk-in">Walk-in</option>
                  <option value="google">Google</option>
                </select>
              </label>

              {/* Ordenar por */}
              <label className="text-sm">
                <span className="block text-gray-500 mb-1">Ordenar por</span>
                <select
                  value={filters.sortBy}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, sortBy: e.target.value }))
                  }
                  className="w-full border border-gray-300 rounded-lg px-2 py-2 text-sm"
                >
                  <option value="recent">Más recientes</option>
                  <option value="reservations">Más {reservationsNoun}</option>
                  <option value="noShows">Más no-shows</option>
                  <option value="lastVisit">Última visita</option>
                  {spendEnabled && <option value="spend">Mayor gasto</option>}
                </select>
              </label>

              {/* Toggles */}
              <div className="flex items-center gap-4 sm:col-span-2 lg:col-span-2 flex-wrap">
                <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.vip}
                    onChange={(e) =>
                      setFilters((f) => ({ ...f, vip: e.target.checked }))
                    }
                    className="rounded border-gray-300"
                  />
                  Solo VIP
                </label>
                <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.blocked}
                    onChange={(e) =>
                      setFilters((f) => ({ ...f, blocked: e.target.checked }))
                    }
                    className="rounded border-gray-300"
                  />
                  Solo bloqueados
                </label>
                <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.inactive}
                    onChange={(e) =>
                      setFilters((f) => ({ ...f, inactive: e.target.checked }))
                    }
                    className="rounded border-gray-300"
                  />
                  Inactivos (6 meses)
                </label>
                {!isAppointmentsMode && (
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.nameMismatch}
                      onChange={(e) =>
                        setFilters((f) => ({
                          ...f,
                          nameMismatch: e.target.checked,
                        }))
                      }
                      className="rounded border-gray-300"
                    />
                    Reservó con otro nombre
                  </label>
                )}
                {activeFilterCount > 0 && (
                  <button
                    onClick={() =>
                      setFilters({
                        hasReservations: "",
                        noShowMin: "",
                        blocked: false,
                        vip: false,
                        inactive: false,
                        source: "",
                        nameMismatch: false,
                        spendMin: "",
                        sortBy: filters.sortBy,
                      })
                    }
                    className="text-sm text-gray-500 hover:text-gray-700 underline ml-auto"
                  >
                    Limpiar filtros
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
          </div>
        ) : loadError ? (
          <div className="text-center py-20 px-4">
            <AlertCircle className="w-16 h-16 text-red-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">
              No se pudieron cargar los clientes
            </h3>
            <p className="text-gray-500 text-sm mb-4">{loadError}</p>
            <button
              onClick={fetchCustomers}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors text-sm"
            >
              Reintentar
            </button>
          </div>
        ) : customers.length === 0 ? (
          <div className="text-center py-20">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">
              {hasActiveSearchOrFilters
                ? "No hay clientes que cumplan los filtros"
                : "No hay clientes"}
            </h3>
            <p className="text-gray-500 text-sm">
              {hasActiveSearchOrFilters
                ? "Prueba a cambiar la búsqueda o quitar algún filtro."
                : isAppointmentsMode
                  ? "Los clientes se crearán automáticamente al pedir cita"
                  : "Los clientes se crearán automáticamente al hacer reservas"}
            </p>
            {hasActiveSearchOrFilters && (
              <button
                onClick={handleClearSearchAndFilters}
                className="mt-4 text-sm text-primary-600 hover:text-primary-700 underline"
              >
                Limpiar búsqueda y filtros
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="md:hidden divide-y divide-gray-100">
              {customers.map((customer) => (
                <div
                  key={customer.id}
                  className="p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-primary-700 font-semibold text-sm">
                          {customer.firstName?.[0]}
                          {customer.lastName?.[0]}
                        </span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-gray-900 truncate flex items-center gap-1.5 flex-wrap">
                          <button
                            onClick={() => setDetailCustomerId(customer.id)}
                            className="hover:text-primary-600 hover:underline text-left"
                          >
                            {customer.firstName} {customer.lastName}
                          </button>
                          {customer.vipStatus && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                              VIP
                            </span>
                          )}
                          {customer.blocked && (
                            <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                              <Ban className="w-3 h-3" /> Bloqueado
                            </span>
                          )}
                          {(customer.noShowCount ?? 0) > 0 && (
                            <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-700">
                              <XCircle className="w-3 h-3" />
                              {customer.noShowCount} no-show
                              {(customer.noShowCount ?? 0) > 1 ? "s" : ""}
                            </span>
                          )}
                          <EmailStatusBadge status={customer.emailStatus} />
                          <BookingNameMismatchBadge
                            names={customer.bookingNameMismatches}
                          />
                        </p>
                        <p className="flex items-center gap-1 text-sm text-gray-600 mt-0.5">
                          <Phone className="w-3 h-3 text-gray-400" />
                          {customer.countryCode} {customer.phone}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => setDetailCustomerId(customer.id)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Ver ficha"
                      >
                        <Eye className="w-4 h-4 text-gray-500" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedCustomer(customer);
                          setShowModal(true);
                        }}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Editar"
                      >
                        <Edit2 className="w-4 h-4 text-gray-500" />
                      </button>
                      {canDeleteCustomers && (
                        <button
                          onClick={() => setShowDeleteConfirm(customer.id)}
                          className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="mt-2 flex items-center gap-3 flex-wrap text-xs text-gray-500 ml-13">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {customer._count?.reservations || 0} {reservationsNoun}
                    </span>
                    {customer.spend && (
                      <span className="font-medium text-gray-700">
                        {formatCents(customer.spend.totalCents, customer.spend.currency)}
                      </span>
                    )}
                    {customer.primarySource &&
                      (() => {
                        const sc = getSourceConfig(customer.primarySource);
                        const Icon = sc.icon;
                        return (
                          <span
                            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${sc.bgColor} ${sc.color}`}
                          >
                            <Icon className="w-3 h-3" />
                            {sc.label}
                          </span>
                        );
                      })()}
                    <span>
                      Desde {safeFormatDate(customer.createdAt, "d MMM yy")}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Cliente
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Contacto
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      {reservationsNounCap}
                    </th>
                    {spendEnabled && (
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Gasto (24 meses)
                      </th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Canal
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Registrado
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {customers.map((customer) => (
                    <tr
                      key={customer.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                            <span className="text-primary-700 font-semibold text-sm">
                              {customer.firstName?.[0]}
                              {customer.lastName?.[0]}
                            </span>
                          </div>
                          <div>
                            <button
                              onClick={() => setDetailCustomerId(customer.id)}
                              className="font-medium text-gray-900 hover:text-primary-600 hover:underline text-left"
                            >
                              {customer.firstName} {customer.lastName}
                            </button>
                            <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                              {customer.vipStatus && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                                  VIP
                                </span>
                              )}
                              {customer.blocked && (
                                <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                                  <Ban className="w-3 h-3" /> Bloqueado
                                </span>
                              )}
                              <BookingNameMismatchBadge
                                names={customer.bookingNameMismatches}
                              />
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          <p className="flex items-center gap-2 text-sm text-gray-600">
                            <Phone className="w-4 h-4 text-gray-400" />
                            {customer.countryCode} {customer.phone}
                          </p>
                          <p className="flex items-center gap-2 text-sm text-gray-500">
                            <Mail className="w-4 h-4 text-gray-400" />
                            {customer.email || "Sin email"}
                          </p>
                          <EmailStatusBadge status={customer.emailStatus} />
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span
                            className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-100 rounded-full text-sm font-medium text-gray-700"
                            title={reservationsNounCap}
                          >
                            <Calendar className="w-4 h-4" />
                            {customer._count?.reservations || 0}
                          </span>
                          {(customer.noShowCount ?? 0) > 0 && (
                            <span
                              className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-200 rounded-full text-sm font-medium text-gray-700"
                              title={`${customer.noShowCount} no-shows`}
                            >
                              <XCircle className="w-4 h-4" />
                              {customer.noShowCount}
                            </span>
                          )}
                        </div>
                      </td>
                      {spendEnabled && (
                        <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                          {customer.spend ? (
                            <>
                              <span className="font-medium">
                                {formatCents(customer.spend.totalCents, customer.spend.currency)}
                              </span>
                              <span className="block text-xs text-gray-500">
                                media{" "}
                                {formatCents(
                                  Math.round(customer.spend.totalCents / customer.spend.visits),
                                  customer.spend.currency,
                                )}
                              </span>
                            </>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                      )}
                      <td className="px-6 py-4">
                        {customer.primarySource ? (
                          (() => {
                            const sc = getSourceConfig(customer.primarySource);
                            const Icon = sc.icon;
                            return (
                              <span
                                className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${sc.bgColor} ${sc.color}`}
                                title={`Canal habitual: ${sc.label}`}
                              >
                                <Icon className="w-3.5 h-3.5" />
                                {sc.label}
                              </span>
                            );
                          })()
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {safeFormatDate(customer.createdAt, "d MMM yyyy")}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => setDetailCustomerId(customer.id)}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Ver ficha"
                          >
                            <Eye className="w-4 h-4 text-gray-500" />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedCustomer(customer);
                              setShowModal(true);
                            }}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Editar"
                          >
                            <Edit2 className="w-4 h-4 text-gray-500" />
                          </button>
                          {canDeleteCustomers && (
                            <button
                              onClick={() => setShowDeleteConfirm(customer.id)}
                              className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                              title="Eliminar"
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 md:px-6 py-4 border-t border-gray-200">
                <p className="text-sm text-gray-500">
                  Mostrando {(pagination.page - 1) * pagination.limit + 1} -{" "}
                  {Math.min(
                    pagination.page * pagination.limit,
                    pagination.total,
                  )}{" "}
                  de {pagination.total}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() =>
                      setPagination((prev) => ({
                        ...prev,
                        page: prev.page - 1,
                      }))
                    }
                    disabled={pagination.page === 1}
                    className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() =>
                      setPagination((prev) => ({
                        ...prev,
                        page: prev.page + 1,
                      }))
                    }
                    disabled={pagination.page === pagination.totalPages}
                    className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              ¿Eliminar cliente?
            </h3>
            <p className="text-gray-500 text-sm mb-6">
              Esta acción no se puede deshacer. Se eliminará toda la información
              del cliente.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="flex-1 py-2 px-4 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleDelete(showDeleteConfirm)}
                className="flex-1 py-2 px-4 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete With Reservations Confirmation Modal (Double Safety) */}
      {deleteWithReservations && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  ⚠️ Atención: Cliente con {reservationsNoun}
                </h3>
              </div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-red-800 text-sm font-medium">
                Este cliente tiene{" "}
                <span className="font-bold">
                  {deleteWithReservations.reservationsCount}{" "}
                  {reservationNounSingular}
                  {deleteWithReservations.reservationsCount > 1 ? "s" : ""}
                </span>{" "}
                asociadas.
              </p>
              <p className="text-red-700 text-sm mt-2">
                Si continúas, se eliminarán tanto el cliente como todas sus{" "}
                {reservationsNoun}. Esta acción es irreversible.
              </p>
            </div>
            <p className="text-gray-600 text-sm mb-6">
              ¿Estás completamente seguro de que deseas eliminar este cliente y
              todas sus {reservationsNoun}?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteWithReservations(null)}
                className="flex-1 py-2.5 px-4 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() =>
                  handleDelete(deleteWithReservations.customerId, true)
                }
                className="flex-1 py-2.5 px-4 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
              >
                Sí, eliminar todo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ficha 360 del cliente */}
      {detailCustomerId && (
        <CustomerDetailModal
          customerId={detailCustomerId}
          initialEmailStatus={
            customers.find((cu) => cu.id === detailCustomerId)?.emailStatus
          }
          onClose={() => setDetailCustomerId(null)}
          onEdit={(id) => {
            const c = customers.find((cu) => cu.id === id) || null;
            setDetailCustomerId(null);
            setSelectedCustomer(c);
            setShowModal(true);
          }}
          onChanged={() => {
            fetchCustomers();
            fetchEmailStatusCounts();
          }}
        />
      )}

      {/* Customer Edit Modal */}
      {showModal && (
        <CustomerEditModal
          customer={selectedCustomer}
          onClose={() => {
            setShowModal(false);
            setSelectedCustomer(null);
          }}
          onSave={() => {
            setShowModal(false);
            setSelectedCustomer(null);
            refreshAfterMutation();
          }}
        />
      )}

      {/* Import Modal */}
      {showImportModal && (
        <ImportModal
          onClose={() => setShowImportModal(false)}
          onSuccess={() => {
            setShowImportModal(false);
            refreshAfterMutation();
          }}
          onDownloadTemplate={handleDownloadTemplate}
        />
      )}

      {/* Duplicates Modal */}
      {showDuplicatesModal && (
        <DuplicatesModal
          reservationsNoun={reservationsNoun}
          onClose={() => {
            // Puede haberse resuelto algún grupo (fusión/descarte) sin llegar
            // a vaciar la lista entera (eso ya dispara `onSuccess`): refrescar
            // también aquí para que el contador exterior de duplicados no se
            // quede desactualizado tras cerrar con la X.
            setShowDuplicatesModal(false);
            refreshAfterMutation();
          }}
          onSuccess={() => {
            setShowDuplicatesModal(false);
            refreshAfterMutation();
          }}
        />
      )}
    </div>
  );
}

// Customer Modal Component
interface ImportModalProps {
  onClose: () => void;
  onSuccess: () => void;
  onDownloadTemplate: () => void;
}

interface ImportResult {
  created: number;
  skipped: number;
  errors: { row: number; reason: string }[];
}

function ImportModal({
  onClose,
  onSuccess,
  onDownloadTemplate,
}: ImportModalProps) {
  const [step, setStep] = useState<"upload" | "preview" | "result">("upload");
  const [csvContent, setCsvContent] = useState<string>("");
  const [fileName, setFileName] = useState<string>("");
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [previewRows, setPreviewRows] = useState<string[][]>([]);
  const [previewHeaders, setPreviewHeaders] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith(".csv")) {
      setError("Por favor, selecciona un archivo CSV");
      return;
    }

    setFileName(file.name);
    setError(null);

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      setCsvContent(content);

      // Parse for preview
      const lines = content.split(/\r?\n/).filter((line) => line.trim());
      if (lines.length > 0) {
        const headers = lines[0]
          .split(",")
          .map((h) => h.trim().replace(/^"|"$/g, ""));
        setPreviewHeaders(headers);

        // Get first 5 data rows for preview
        const dataRows = lines
          .slice(1, 6)
          .map((line) =>
            line.split(",").map((v) => v.trim().replace(/^"|"$/g, "")),
          );
        setPreviewRows(dataRows);
        setStep("preview");
      }
    };
    reader.onerror = () => {
      setError("Error al leer el archivo");
    };
    reader.readAsText(file, "UTF-8");
  };

  const handleImport = async () => {
    if (!csvContent) {
      setError("No hay contenido para importar");
      return;
    }

    setImporting(true);
    setError(null);

    try {
      const response = await api.post("/customers/import", {
        csvContent,
        skipDuplicates,
      });
      setResult(response.data.data);
      setStep("result");
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { message?: string } } };
      setError(
        axiosError.response?.data?.message || "Error al importar clientes",
      );
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    if (result && result.created > 0) {
      onSuccess();
    } else {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-xl max-h-[90dvh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-5 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            {step === "upload" && "Importar Clientes"}
            {step === "preview" && "Vista Previa"}
            {step === "result" && "Resultado de la Importación"}
          </h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 overflow-y-auto flex-1">
          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-700 text-sm rounded-lg flex items-center gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {step === "upload" && (
            <div className="space-y-6">
              {/* Download Template */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-start gap-3">
                  <FileText className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-medium text-blue-900">
                      Paso 1: Descarga la plantilla
                    </h3>
                    <p className="text-sm text-blue-700 mt-1">
                      Descarga la plantilla CSV, rellénala con tus clientes y
                      súbela aquí.
                    </p>
                    <button
                      onClick={onDownloadTemplate}
                      className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      Descargar Plantilla
                    </button>
                  </div>
                </div>
              </div>

              {/* Upload Area */}
              <div>
                <h3 className="font-medium text-gray-900 mb-3">
                  Paso 2: Sube tu archivo
                </h3>
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-primary-500 hover:bg-primary-50 transition-colors"
                >
                  <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">
                    <span className="text-primary-600 font-medium">
                      Haz clic para seleccionar
                    </span>{" "}
                    o arrastra tu archivo CSV aquí
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    Solo archivos .csv
                  </p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>

              {/* Format Info */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">
                  Formato del CSV
                </h4>
                <p className="text-sm text-gray-600 mb-2">
                  El archivo debe contener las siguientes columnas (los nombres
                  son flexibles):
                </p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>
                    • <strong>Nombre</strong> (requerido) - Nombre del cliente
                  </li>
                  <li>
                    • <strong>Apellido</strong> - Apellidos del cliente
                  </li>
                  <li>
                    • <strong>Teléfono</strong> (requerido) - Número de teléfono
                  </li>
                  <li>
                    • <strong>Correo Electrónico</strong> - Email del cliente
                  </li>
                  <li>
                    • <strong>Código País</strong> - Prefijo telefónico (+34 por
                    defecto)
                  </li>
                  <li>
                    • <strong>Notas</strong> - Observaciones o comentarios
                  </li>
                  <li>
                    • <strong>VIP</strong> - sí/no
                  </li>
                </ul>
              </div>
            </div>
          )}

          {step === "preview" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">
                    Archivo:{" "}
                    <span className="font-medium text-gray-900">
                      {fileName}
                    </span>
                  </p>
                  <p className="text-sm text-gray-500">
                    {previewRows.length} filas de ejemplo (mostrando máximo 5)
                  </p>
                </div>
              </div>

              {/* Preview Table */}
              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      {previewHeaders.map((header, i) => (
                        <th
                          key={i}
                          className="px-3 py-2 text-left font-medium text-gray-600 whitespace-nowrap"
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {previewRows.map((row, rowIndex) => (
                      <tr key={rowIndex} className="hover:bg-gray-50">
                        {row.map((cell, cellIndex) => (
                          <td
                            key={cellIndex}
                            className="px-3 py-2 text-gray-700 whitespace-nowrap max-w-[200px] truncate"
                          >
                            {cell || (
                              <span className="text-gray-400 italic">
                                vacío
                              </span>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Options */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={skipDuplicates}
                    onChange={(e) => setSkipDuplicates(e.target.checked)}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <div>
                    <span className="font-medium text-gray-900">
                      Omitir duplicados
                    </span>
                    <p className="text-sm text-gray-500">
                      Si el teléfono ya existe, se omitirá el registro. Si
                      desactivas esta opción, se actualizarán los datos
                      existentes.
                    </p>
                  </div>
                </label>
              </div>
            </div>
          )}

          {step === "result" && result && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2 text-green-700 mb-1">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">Creados</span>
                  </div>
                  <p className="text-2xl font-bold text-green-900">
                    {result.created}
                  </p>
                </div>
                <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <div className="flex items-center gap-2 text-yellow-700 mb-1">
                    <AlertCircle className="w-5 h-5" />
                    <span className="font-medium">Omitidos</span>
                  </div>
                  <p className="text-2xl font-bold text-yellow-900">
                    {result.skipped}
                  </p>
                </div>
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <div className="flex items-center gap-2 text-red-700 mb-1">
                    <X className="w-5 h-5" />
                    <span className="font-medium">Errores</span>
                  </div>
                  <p className="text-2xl font-bold text-red-900">
                    {result.errors.length}
                  </p>
                </div>
              </div>

              {/* Errors list */}
              {result.errors.length > 0 && (
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <h4 className="font-medium text-red-900 mb-2">
                    Detalles de errores:
                  </h4>
                  <ul className="text-sm text-red-700 space-y-1 max-h-40 overflow-y-auto">
                    {result.errors.map((err, i) => (
                      <li key={i}>
                        Fila {err.row}: {err.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {result.created > 0 && (
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-green-700">
                    ¡Se han importado correctamente {result.created} clientes!
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-5 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
          {step === "upload" && (
            <button
              onClick={onClose}
              className="flex-1 py-2.5 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
          )}
          {step === "preview" && (
            <>
              <button
                onClick={() => {
                  setStep("upload");
                  setCsvContent("");
                  setFileName("");
                  setPreviewRows([]);
                  setPreviewHeaders([]);
                }}
                className="flex-1 py-2.5 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
              >
                Volver
              </button>
              <button
                onClick={handleImport}
                disabled={importing}
                className="flex-1 py-2.5 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {importing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Importando...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Importar Clientes
                  </>
                )}
              </button>
            </>
          )}
          {step === "result" && (
            <button
              onClick={handleClose}
              className="flex-1 py-2.5 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
            >
              Cerrar
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Duplicates Modal Component
interface DuplicatesModalProps {
  onClose: () => void;
  onSuccess: () => void;
  /** "reservas" o "citas" según `bookingMode`, ya calculado por el padre. */
  reservationsNoun: string;
}

function DuplicatesModal({
  onClose,
  onSuccess,
  reservationsNoun,
}: DuplicatesModalProps) {
  const [loading, setLoading] = useState(true);
  const [duplicateGroups, setDuplicateGroups] = useState<DuplicateGroup[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<DuplicateGroup | null>(
    null,
  );
  const [primaryId, setPrimaryId] = useState<string>("");
  const [merging, setMerging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    fetchDuplicates();
  }, []);

  const fetchDuplicates = async () => {
    setLoading(true);
    try {
      const response = await api.get("/customers/duplicates");
      setDuplicateGroups(response.data.data?.groups || []);
    } catch (err) {
      console.error("Error fetching duplicates:", err);
      setError("Error al cargar duplicados");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectGroup = (group: DuplicateGroup) => {
    setSelectedGroup(group);
    // Pre-select the customer with more reservations as primary
    const sorted = [...group.customers].sort(
      (a, b) => (b._count?.reservations || 0) - (a._count?.reservations || 0),
    );
    setPrimaryId(sorted[0]?.id || "");
    setError(null);
    setSuccessMessage(null);
  };

  const handleMerge = async () => {
    if (!selectedGroup || !primaryId) return;

    const secondaryIds = selectedGroup.customers
      .filter((c) => c.id !== primaryId)
      .map((c) => c.id);

    if (secondaryIds.length === 0) {
      setError("Selecciona al menos un cliente para fusionar");
      return;
    }

    setMerging(true);
    setError(null);

    try {
      const primaryCustomer = selectedGroup.customers.find(
        (c) => c.id === primaryId,
      );

      const response = await api.post("/customers/merge", {
        primaryId,
        secondaryIds,
        mergedData: primaryCustomer, // Keep primary customer data
      });

      setSuccessMessage(response.data.message);

      // Remove the merged group from the list
      setDuplicateGroups((prev) =>
        prev.filter((g) => g.key !== selectedGroup.key),
      );
      setSelectedGroup(null);
      setPrimaryId("");

      // If no more duplicates, close modal
      if (duplicateGroups.length <= 1) {
        setTimeout(() => {
          onSuccess();
        }, 1500);
      }
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { message?: string } } };
      setError(
        axiosError.response?.data?.message || "Error al fusionar clientes",
      );
    } finally {
      setMerging(false);
    }
  };

  const handleDismiss = async () => {
    if (!selectedGroup) return;

    const customerIds = selectedGroup.customers.map((c) => c.id);

    setMerging(true); // Reuse merging state for loading
    setError(null);

    try {
      const response = await api.post("/customers/dismiss-duplicate", {
        customerIds,
      });

      setSuccessMessage(response.data.message);

      // Remove the dismissed group from the list
      setDuplicateGroups((prev) =>
        prev.filter((g) => g.key !== selectedGroup.key),
      );
      setSelectedGroup(null);
      setPrimaryId("");

      // If no more duplicates, close modal
      if (duplicateGroups.length <= 1) {
        setTimeout(() => {
          onSuccess();
        }, 1500);
      }
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { message?: string } } };
      setError(
        axiosError.response?.data?.message || "Error al marcar como diferentes",
      );
    } finally {
      setMerging(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-4xl shadow-xl max-h-[90dvh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-5 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
              <GitMerge className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Gestión de Duplicados
              </h2>
              <p className="text-sm text-gray-500">
                {duplicateGroups.length} grupo(s) con el mismo teléfono o email
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
            </div>
          ) : duplicateGroups.length === 0 ? (
            <div className="flex-1 flex items-center justify-center p-8 text-center">
              <div>
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  ¡Sin duplicados!
                </h3>
                <p className="text-gray-500">
                  No se encontraron clientes duplicados.
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Groups List */}
              <div className="w-1/3 border-r border-gray-200 overflow-y-auto">
                <div className="p-3">
                  <p className="text-xs font-medium text-gray-500 uppercase mb-2 px-2">
                    Grupos detectados
                  </p>
                  <div className="space-y-1">
                    {duplicateGroups.map((group) => (
                      <button
                        key={group.key}
                        onClick={() => handleSelectGroup(group)}
                        className={`w-full text-left p-3 rounded-lg transition-colors ${
                          selectedGroup?.key === group.key
                            ? "bg-primary-50 border border-primary-200"
                            : "hover:bg-gray-50 border border-transparent"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <AlertTriangle
                            className={`w-4 h-4 flex-shrink-0 ${
                              selectedGroup?.key === group.key
                                ? "text-primary-600"
                                : "text-amber-500"
                            }`}
                          />
                          <span className="font-medium text-gray-900 truncate">
                            {group.customers.length} clientes
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1 truncate pl-6">
                          {group.reason}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Detail View */}
              <div className="flex-1 overflow-y-auto p-5">
                {!selectedGroup ? (
                  <div className="h-full flex items-center justify-center text-gray-500">
                    <div className="text-center">
                      <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p>Selecciona un grupo de duplicados</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {successMessage && (
                      <div className="p-3 bg-green-50 text-green-700 text-sm rounded-lg flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 flex-shrink-0" />
                        {successMessage}
                      </div>
                    )}

                    {error && (
                      <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                        {error}
                      </div>
                    )}

                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-sm text-blue-800">
                        <strong>Razón:</strong> {selectedGroup.reason}
                      </p>
                      <p className="text-xs text-blue-600 mt-1">
                        Selecciona el cliente principal que quieres conservar.
                        Los demás se fusionarán en él.
                      </p>
                    </div>

                    <div className="space-y-3">
                      {selectedGroup.customers.map((customer) => (
                        <div
                          key={customer.id}
                          onClick={() => setPrimaryId(customer.id)}
                          className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                            primaryId === customer.id
                              ? "border-primary-500 bg-primary-50 shadow-sm"
                              : "border-gray-200 hover:border-gray-300 bg-white"
                          }`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex items-center gap-3">
                              <div
                                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                  primaryId === customer.id
                                    ? "border-primary-500 bg-primary-500"
                                    : "border-gray-300"
                                }`}
                              >
                                {primaryId === customer.id && (
                                  <CheckCircle className="w-3 h-3 text-white" />
                                )}
                              </div>
                              <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                                <span className="text-primary-700 font-semibold text-sm">
                                  {customer.firstName?.[0]}
                                  {customer.lastName?.[0]}
                                </span>
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">
                                  {customer.firstName} {customer.lastName}
                                  {customer.vipStatus && (
                                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                                      VIP
                                    </span>
                                  )}
                                </p>
                                <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 mt-1">
                                  <span className="flex items-center gap-1">
                                    <Phone className="w-3 h-3" />
                                    {customer.countryCode} {customer.phone}
                                  </span>
                                  {customer.email &&
                                    !customer.email.includes(
                                      "@imported.local",
                                    ) && (
                                      <span className="flex items-center gap-1">
                                        <Mail className="w-3 h-3" />
                                        {customer.email}
                                      </span>
                                    )}
                                </div>
                              </div>
                            </div>
                            <div className="text-right text-sm">
                              <p className="font-medium text-gray-900">
                                {customer._count?.reservations || 0}{" "}
                                {reservationsNoun}
                              </p>
                              <p className="text-gray-500 text-xs">
                                Creado:{" "}
                                {safeFormatDate(customer.createdAt, "d MMM yyyy")}
                              </p>
                            </div>
                          </div>
                          {customer.notes && (
                            <p className="mt-2 text-sm text-gray-600 pl-8 italic">
                              {customer.notes}
                            </p>
                          )}
                          {primaryId === customer.id && (
                            <div className="mt-2 pl-8">
                              <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-primary-100 text-primary-700">
                                Cliente principal (se conservará)
                              </span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 pt-4">
                      <button
                        onClick={handleDismiss}
                        disabled={merging}
                        className="sm:flex-1 py-2.5 bg-white border border-amber-300 text-amber-700 font-medium rounded-lg hover:bg-amber-50 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                        title="Marcar estos clientes como personas diferentes"
                      >
                        <X className="w-4 h-4" />
                        <span>Son diferentes</span>
                      </button>
                      <button
                        onClick={handleMerge}
                        disabled={merging || !primaryId}
                        className="sm:flex-1 py-2.5 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {merging ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Procesando...
                          </>
                        ) : (
                          <>
                            <GitMerge className="w-4 h-4" />
                            Fusionar Clientes
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
